import React, { Dispatch, Suspense, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Grid,
    IconButton,
    Link,
    Typography
} from '@mui/material';
import { Download, PhotoCamera } from '@mui/icons-material';
import { isFProject } from 'config/project';
import { TransportationModel, TransportationStatus } from 'models/TransportationModel';
import { CompanyType } from 'models/CompanyModel';
import Directions from 'modules/transportation/components/Common';
import useTransportationModals from 'modules/transportation/hooks/useTransportationModals';
import auth from 'recoil/atoms/auth';
import Api from 'utils/api';
import { forceDownload, replaceItemAtIndex } from 'utils/common';


const PendingInner = () => {
    const [transportations, setTransportations] = useState<TransportationModel[]>([]);

    const load = () => {
        Api.transportation
            .all(TransportationStatus.Pending)
            .then(res => setTransportations(res.data));
    };

    useEffect(load, []);

    const groups = transportations.reduce((ret, a) => {
        const key = `${a.from.id}_${a.to.id}`;
        return {...ret, [key]: ret[key] ? [...ret[key], a] : [a]}
    }, {} as Record<string, TransportationModel[]>);

    return (
        <>
            {Object.values(groups).map((group, i) => <TransportGroup
                key={i}
                group={group}
                load={load}
            />)}
        </>
    )
};

type TransportGroupProps = {
    group: TransportationModel[],
    load: () => void,
}

const TransportGroup = ({group, load}: TransportGroupProps) => {
    const {t} = useTranslation();
    const [checked, setChecked] = useState(group.map(() => false));
    const first = group[0];

    const makeOrder = () => {
        const selected = group
            .filter((_, i) => checked[i])
            .map(tr => tr.id);

        if (selected.length)
            Api.transportation.loadMany(selected)
                .then(load);
    }

    return (
        <Card sx={{
            mb: 2,
        }}>
            <CardContent sx={{bgcolor: 'grey.200'}}>
                <Directions transportation={first}/>
            </CardContent>

            <CardContent>
                {group.map((order, i) => <TransportationRow
                    key={i}
                    idx={i}
                    prevAgent={group[i - 1]?.agent.id}
                    transportation={order}
                    checked={checked}
                    setChecked={setChecked}
                />)}
            </CardContent>

            <CardActions sx={{justifyContent: 'flex-end'}}>
                {checked.some(item => item) && <Button onClick={makeOrder} size="small">{t('make_order')}</Button>}
            </CardActions>
        </Card>
    )
}

type TransportationRowProps = {
    idx: number,
    prevAgent: number,
    transportation: TransportationModel,
    checked: boolean[],
    setChecked: Dispatch<React.SetStateAction<boolean[]>>,
}

const TransportationRow = ({idx, transportation, prevAgent, checked, setChecked}: TransportationRowProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {openPhotosModal} = useTransportationModals();

    const download = () => Api.transportation
        .downloadList(transportation.from.id, transportation.to.id, transportation.agent.id)
        .then(forceDownload);

    return (
        <>
            {my.type === CompanyType.Master && prevAgent !== transportation.agent.id && (
                <Typography
                    borderTop="1px solid #eeeeee"
                    color="secondary.main"
                    py={1}
                >
                    {transportation.agent.name}

                    <IconButton
                        onClick={download}
                    >
                        <Download/>
                    </IconButton>
                </Typography>
            )}
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Checkbox
                        checked={checked[idx]}
                        onChange={e => setChecked(old => replaceItemAtIndex(old, idx, e.target.checked))}
                    />
                </Grid>
                <Grid item xs={6} sm={3}>
                    {my.type === CompanyType.Master ?
                        <Link href={`/car/${transportation.car.id}`}>{transportation.car.chassisNumber}</Link>:
                        <>{transportation.car.chassisNumber}</>
                    }
                </Grid>
                <Grid item xs={6} sm={3} sx={{fontSize: {xs: '.75rem', sm: '1rem'}}}>
                    {transportation.car.make} {transportation.car.model} {!!transportation.car.damaged && !isFProject && <Box component="strong" color="error.light">DMG</Box>}
                </Grid>
                <Grid item sm={3}>
                    {transportation.car.length}&times;{transportation.car.width}&times;{transportation.car.height}{t('cm')}
                </Grid>
                <Grid item>
                    <IconButton onClick={() => openPhotosModal(transportation.car, transportation.id)}>
                        <PhotoCamera/>
                    </IconButton>
                </Grid>
            </Grid>
        </>
    )
}

const Pending = () => {
    return (
        <Suspense fallback={null}>
            <PendingInner/>
        </Suspense>
    )
};

export default Pending;
